/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ValidationErrors } from '@angular/forms';
import { type BaseRequestOptionsArgs, type FormControl, type FormGroup, type HttpResponse, type Rest, CAPACITY_UNIT, HttpService, UtilService } from '@dm/core';
import { type Observable } from 'rxjs';

import { type QueryQuotaTypeE, UserQuotaStrService } from './user-quota-str.service';

@Injectable({ providedIn: 'root' })
export class UserQuotaService {
    constructor(
        private http: HttpService,
        private utilService: UtilService,
        private str: UserQuotaStrService,
    ) { }

    /**
     * 获取批量查询的rest
     * @param parentType
     * @param parentId
     * @param queryType 0：所有，1：用户配置，2：配额报告
     */
    getBatchRest(parentType: number, parentId: string, queryType?: QueryQuotaTypeE, vstoreId?: string): Rest {
        const rest = this.http.makeUrl('quota.getBatch');
        rest.setParams({
            PARENTTYPE: parentType,
            PARENTID: parentId,
            QUERYTYPE: queryType,
            SPACEUNITTYPE: 1, // 指定容量单位为KB
        });
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 获取数量rest
     * @param parentType
     * @param parentId
     * @param queryType 0：所有，1：用户配置，2：配额报告
     */
    getCountRest(parentType: number, parentId: string, queryType?: QueryQuotaTypeE, vstoreId?: string): Rest {
        const rest = this.http.makeUrl('quota.count');
        rest.setParams({
            PARENTTYPE: parentType,
            PARENTID: parentId,
            QUERYTYPE: queryType,
            SPACEUNITTYPE: 1, // 指定容量单位为KB
        });
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 获取批量数据
     * @param parentType
     * @param parentId
     * @param queryType 0：所有，1：用户配置，2：配额报告
     */
    getBatch(parentType: number, parentId: string, range: number[], vstoreId?: string, queryType: QueryQuotaTypeE = 1,
        options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getBatchRest(parentType, parentId, queryType, vstoreId)
            .setParams({
                range: `[${range[0]}-${range[1]}]`,
            });
        return this.http.get(rest, options);
    }

    /**
     * 获取数量
     * @param parentType
     * @param parentId
     * @param queryType 0：所有，1：用户配置，2：配额报告
     */
    getCount(parentType: number, parentId: string, vstoreId?: string, queryType: QueryQuotaTypeE = 1, options?: BaseRequestOptionsArgs)
        : Observable<HttpResponse> {
        const rest = this.getCountRest(parentType, parentId, queryType, vstoreId);
        return this.http.get(rest, options);
    }

    get(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('quota.get').format(id);

        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options);
    }

    create(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('quota.post');
        return this.http.post(rest, params, options);
    }

    modify(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('quota.put').format(id);
        return this.http.put(rest, params, options);
    }

    delete(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('quota.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }

    /**
     * 查询文件系统rest
     */
    getFileSystemRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 查询Dtree rest
     */
    getDtreeRest(fileSystemId: string, vstoreId?: string): Rest {
        const rest = this.http.makeUrl('dtree.getBatch').setParams({
            PARENTID: fileSystemId,
        });
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 获取UNIX用户rest
     */
    getUnixUserRest(vstoreId: string): Rest {
        const rest = this.http.makeUrl('UnixLocalUser.getBatch').setParams({ vstoreId });
        return rest;
    }

    /**
     * 获取UNIX用户组rest
     */
    getUnixUserGroupRest(vstoreId: string): Rest {
        const rest = this.http.makeUrl('UnixLocalGroup.getBatch').setParams({ vstoreId });
        return rest;
    }

    /**
     * windows 本地用户 rest
     */
    batchWindowsUserRest(vstoreId: string): Rest {
        const rest = this.http.makeUrl('windowsUser.getBatch').setParams({ vstoreId });
        return rest;
    }

    /**
     * windows 本地用户组 rest
     */
    batchWindowsUserGroupRest(vstoreId: string): Rest {
        const rest = this.http.makeUrl('WindowsLocalGroup.getBatch').setParams({ vstoreId });
        return rest;
    }

    /**
     * 空间配额值比较
     */
    spaceCompareValidator = (control: FormGroup): ValidationErrors | null => {
        if (!control) {
            return null;
        }
        const hard = control.value.hard;
        const soft = control.value.soft;

        const hardValue: number = this.utilService.adaptUnit(hard.value, hard.unit, CAPACITY_UNIT.KB).value;
        const softValue: number = this.utilService.adaptUnit(soft.value, soft.unit, CAPACITY_UNIT.KB).value;

        // 获取最大值范围
        const arr = this.str.spaceQuotaMax.split(' ');
        const spaceQuotaMaxValue = arr[0];
        const spaceQuotaMaxUnit = arr[1];
        // 计算出根据最大值的单位转为KB后的值，方便与硬配额或软配额输入的值进行比较
        const spaceQuotaMaxValueResult: number = this.utilService.adaptUnit(spaceQuotaMaxValue, spaceQuotaMaxUnit, CAPACITY_UNIT.KB).value;

        // 硬配额是否超过最大值
        const hardValueQuotaOutRange = hardValue && spaceQuotaMaxValueResult < hardValue;
        // 软配额是否超过最大值
        const softValueQuotaOutRange = softValue && spaceQuotaMaxValueResult < softValue;

        (control.get('hard') as FormControl).clearError('max');
        (control.get('soft') as FormControl).clearError('max');

        if (hardValueQuotaOutRange || softValueQuotaOutRange) {
            // 硬配额超过最大值提示对应的错误信息
            if (hardValueQuotaOutRange) {
                control.get('hard').setErrors({ max: true });
            }
            // 软配额超过最大值提示对应的错误信息
            if (softValueQuotaOutRange) {
                control.get('soft').setErrors({ max: true });
            }
            return null;
        }

        if (hardValue && softValue && hardValue <= softValue) {
            control.get('hard').markAsTouched();
            control.get('soft').markAsTouched();
            control.get('hard').setErrors({ quotaCompare: true });
            control.get('soft').setErrors({ quotaCompare: true });
            return { quotaCompare: true };
        }
        (control.get('hard') as FormControl).clearError('quotaCompare');
        (control.get('soft') as FormControl).clearError('quotaCompare');
        return null;
    };

    /**
     * 文件数配额值比较
     */
    fileCompareValidator = (control: FormGroup): ValidationErrors | null => {
        if (!control) {
            return null;
        }
        const hard = control.get('hard') as FormControl;
        const soft = control.get('soft') as FormControl;
        const hardUnit = control.get('hardUnit') as FormControl;
        const softUnit = control.get('softUnit') as FormControl;

        const hardValue: number = hard.value * hardUnit.value;
        const softValue: number = soft.value * softUnit.value;

        hard.markAsTouched();
        hardUnit.markAsTouched();
        soft.markAsTouched();
        softUnit.markAsTouched();

        if (hardValue || softValue) {
            const hardValueQuotaOutRange = hardValue && this.str.fileQuotaMax < hardValue;
            const softValueQuotaOutRange = softValue && this.str.fileQuotaMax < softValue;

            hard.clearError();
            soft.clearError();
            hardUnit.clearError();
            softUnit.clearError();

            if (hardValueQuotaOutRange || softValueQuotaOutRange) {
                if (hardValueQuotaOutRange) {
                    hard.setErrors({ fileHardQuota: true });
                    hardUnit.setErrors({ fileHardQuota: true });
                }
                if (softValueQuotaOutRange) {
                    soft.setErrors({ fileSoftQuota: true });
                    softUnit.setErrors({ fileSoftQuota: true });
                }
                return null;
            }
            // 硬配额值需要大于软配额值
            if (hardValue && softValue && hardValue <= softValue) {
                hard.setErrors({ quotaCompare: true });
                soft.setErrors({ quotaCompare: true });
                hardUnit.setErrors({ quotaCompare: true });
                softUnit.setErrors({ quotaCompare: true });
                return { quotaCompare: true };
            }
        }
        return null;
    };
}
